import React from 'react';
import companyInfo from '../../jsons/companyInfo.json';
import './MeltingConsentForm.css';

/**
 * Form AGC/01 — "Customer Consent & Authorization for Melting and Purity
 * Testing", laid out to the printed original.
 *
 * Rendered off-screen and rasterised by html2pdf, so it must stay free of
 * anything html2canvas cannot draw (see MeltingConsentForm.css). The roundel
 * is served from /logo512.png — same origin, so the canvas stays untainted.
 *
 * Purely presentational: everything it prints comes from `data`, built by
 * buildConsentData() in ./index.jsx.
 */

const LOGO = `${process.env.PUBLIC_URL || ''}/logo512.png`;

const GoldRibbon = () => (
  <svg className="mcf-ribbon" viewBox="0 0 794 44" preserveAspectRatio="none"
    width="794" height="30" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="mcfGold" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#8a6216" />
        <stop offset="22%" stopColor="#d8ae53" />
        <stop offset="45%" stopColor="#f4e3b8" />
        <stop offset="72%" stopColor="#cd9d34" />
        <stop offset="100%" stopColor="#8a6216" />
      </linearGradient>
    </defs>
    <path d="M0,0 C210,30 520,32 794,4 L794,11 C520,40 210,38 0,13 Z" fill="url(#mcfGold)" />
    <path d="M0,16 C200,38 470,40 700,24 L700,27 C470,43 200,41 0,20 Z" fill="url(#mcfGold)" opacity="0.75" />
  </svg>
);

const ShieldIcon = () => (
  <svg width="17" height="19" viewBox="0 0 17 19" xmlns="http://www.w3.org/2000/svg">
    <path d="M8.5 0.8 L16 3.4 V9 c0 5-3.2 8.1-7.5 9.3C4.2 17.1 1 14 1 9 V3.4 Z"
      fill="#f6e2b4" stroke="#c8971f" strokeWidth="1.1" />
    <path d="M5.2 9.2 L7.6 11.6 L11.9 6.9" fill="none" stroke="#b8801f" strokeWidth="1.6"
      strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const TickBox = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" xmlns="http://www.w3.org/2000/svg">
    <rect x="0.6" y="0.6" width="11.8" height="11.8" rx="1.8" fill="#fff" stroke="#cf9b30" strokeWidth="1.2" />
    <path d="M3 6.6 L5.3 8.9 L10 3.9" fill="none" stroke="#cf9b30" strokeWidth="1.7"
      strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Flourish = () => (
  <svg width="230" height="12" viewBox="0 0 230 12" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 6 H92" stroke="#cfa54a" strokeWidth="1.2" strokeLinecap="round" />
    <path d="M138 6 H222" stroke="#cfa54a" strokeWidth="1.2" strokeLinecap="round" />
    <path d="M100 6 L104 2 L108 6 L104 10 Z" fill="#cfa54a" />
    <path d="M122 6 L126 2 L130 6 L126 10 Z" fill="#cfa54a" />
    <circle cx="115" cy="6" r="2.2" fill="#cfa54a" />
  </svg>
);

const PhoneIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" xmlns="http://www.w3.org/2000/svg">
    <path d="M3.2 1.2 L5 1.2 L6 4 L4.7 5 c0.8 1.6 1.9 2.7 3.5 3.5 L9.2 7.2 L12 8.2 v1.8
      c0 0.6-0.5 1.1-1.1 1 C5.4 10.4 2.9 7.9 2.2 2.3 A1 1 0 0 1 3.2 1.2 Z" fill="#ffffff" />
  </svg>
);

const GlobeIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" xmlns="http://www.w3.org/2000/svg">
    <circle cx="6.5" cy="6.5" r="5.6" fill="none" stroke="#ffffff" strokeWidth="1.1" />
    <ellipse cx="6.5" cy="6.5" rx="2.4" ry="5.6" fill="none" stroke="#ffffff" strokeWidth="1.1" />
    <path d="M1.2 6.5 H11.8" stroke="#ffffff" strokeWidth="1.1" />
  </svg>
);

const PinIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" xmlns="http://www.w3.org/2000/svg">
    <path d="M6.5 0.9 c2.3 0 4.1 1.8 4.1 4.1 0 3-4.1 7.1-4.1 7.1 S2.4 8 2.4 5
      c0-2.3 1.8-4.1 4.1-4.1 Z" fill="#ffffff" />
    <circle cx="6.5" cy="5" r="1.5" fill="#6d3a12" />
  </svg>
);

/** One "Label : value" cell inside the sections 1–2 grid. */
const KvCell = ({ label, value, wide }) => (
  <div className="mcf-kv__cell">
    <span className={`mcf-kv__label${wide ? ' mcf-kv__label--wide' : ''}`}>{label}</span>
    <span className="mcf-kv__colon">:</span>
    <span className="mcf-kv__value" title={value || ''}>{value || ''}</span>
  </div>
);

const Declaration = ({ children }) => (
  <div className="mcf-decl">
    <span className="mcf-decl__tick"><TickBox /></span>
    <span className="mcf-decl__text">{children}</span>
  </div>
);

const MeltingConsentDocument = React.forwardRef(({ data }, ref) => {
  const {
    formNo, dateStr, timeStr,
    // eslint-disable-next-line no-unused-vars -- read by the parked table columns below
    metalLabel,
    metalLower,
    customer, branchName, employeeName, employeeCode, items, totals, photos, website,
  } = data;

  return (
    // Photographs eat roughly a table's worth of height, so they pull the page
    // into the dense layout the same way a long item list does.
    <div className={`mcf-page${items.length > 3 || photos.length > 0 ? ' mcf-page--dense' : ''}`} ref={ref}>
      {/* ── Masthead ── */}
      <div className="mcf-head">
        <div className="mcf-brand">
          <img className="mcf-brand__mark" src={LOGO} alt="" crossOrigin="anonymous" />
          <div className="mcf-brand__text">
            <div className="mcf-brand__name">AURIKSHA</div>
            <div className="mcf-brand__tagline">
              <span className="mcf-brand__rule" />
              <span style={{ padding: '0 8px' }}>THE MOST TRUSTED GOLD BUYER</span>
              <span className="mcf-brand__rule" />
            </div>
          </div>
        </div>

        <div className="mcf-formbox">
          <div className="mcf-formbox__row">
            <span className="mcf-formbox__label">Form No. :</span>
            <span className="mcf-formbox__val">{formNo}</span>
          </div>
          <div className="mcf-formbox__row">
            <span className="mcf-formbox__label">Date :</span>
            <span className="mcf-formbox__val">{dateStr}</span>
          </div>
          <div className="mcf-formbox__row">
            <span className="mcf-formbox__label">Time :</span>
            <span className="mcf-formbox__val">{timeStr}</span>
          </div>
        </div>
      </div>

      <GoldRibbon />

      {/* ── Title ── */}
      <div className="mcf-title">
        <div className="mcf-title__main">
          Customer Consent &amp; Authorization<br />for Melting and Purity Testing
        </div>
        <div className="mcf-title__sub">
          To be completed and signed by the customer before melting the jewellery.
        </div>
      </div>

      <div className="mcf-body">
        {/* ── 1. Customer details ── */}
        <div className="mcf-sec">
          <div className="mcf-sec__tab">1. CUSTOMER DETAILS</div>
          <div className="mcf-sec__box mcf-sec__box--flush">
            <table className="mcf-kv">
              <tbody>
                <tr>
                  <td><KvCell label="Customer Name" value={customer.name} /></td>
                  <td><KvCell label="Mobile No." value={customer.mobile} /></td>
                </tr>
                <tr>
                  <td colSpan={2}><KvCell label="Address" value={customer.address} /></td>
                </tr>
                <tr>
                  <td><KvCell label="ID Proof Type" value={customer.idProofType} /></td>
                  <td><KvCell label="ID Proof No." value={customer.idProofNo} /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* ── 2. Jewellery details ── */}
        <div className="mcf-sec">
          <div className="mcf-sec__tab">
            2. JEWELLERY DETAILS <span style={{ fontWeight: 400, fontSize: 11 }}>(As examined before melting)</span>
          </div>
          <div className="mcf-sec__box mcf-sec__box--flush" style={{ border: 'none' }}>
            <table className="mcf-table">
              {/* Non-Metal Weight, Net Estimated Weight and Remarks are parked, not
                  deleted. To bring a column back, uncomment its <col>, its <th> and
                  its <td> together, and restore the six-column widths noted here. */}
              <colgroup>
                <col style={{ width: '8%' }} />{/* six-column width: 7% */}
                <col style={{ width: '60%' }} />{/* six-column width: 30% */}
                <col style={{ width: '32%' }} />{/* six-column width: 15% */}
                {/* <col style={{ width: '18%' }} /> */}
                {/* <col style={{ width: '16%' }} /> */}
                {/* <col style={{ width: '14%' }} /> */}
              </colgroup>
              <thead>
                <tr>
                  <th>Sl.<small>No.</small></th>
                  <th>Description of Jewellery<small>(Article wise)</small></th>
                  <th>Gross Weight<small>(grams)</small></th>
                  {/* <th>Non-{metalLabel} Weight<small>(stones, beads, etc.)</small><small>(grams)</small></th> */}
                  {/* <th>Net Estimated<small>{metalLabel} Weight</small><small>(grams)</small></th> */}
                  {/* <th>Remarks</th> */}
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.sl}>
                    <td className="mcf-num">{it.sl}.</td>
                    <td>{it.description}</td>
                    <td className="mcf-wt">{it.grossWeight}</td>
                    {/* <td className="mcf-wt">{it.nonMetalWeight}</td> */}
                    {/* <td className="mcf-wt">{it.netWeight}</td> */}
                    {/* <td>{it.remarks}</td> */}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mcf-table__totals">
              <div className="mcf-total">
                <span className="mcf-total__label">Total Gross Weight :</span>
                <span className="mcf-total__value">{totals.gross}</span>
                <span className="mcf-total__unit">g</span>
              </div>
              {/* Parked with the Net Estimated Weight column above. */}
              {/* <div className="mcf-total mcf-total--right">
                <span className="mcf-total__label">Total Net Estimated {metalLabel} Weight :</span>
                <span className="mcf-total__value">{totals.net}</span>
                <span className="mcf-total__unit">g</span>
              </div> */}
            </div>
            {photos.length > 0 && (
              <div className="mcf-photos">
                <div className="mcf-photos__caption">
                  Photographs of the jewellery as examined before melting
                </div>
                <div className="mcf-photos__strip">
                  {photos.map((src, i) => (
                    // Background image rather than <img>: html2canvas draws
                    // background-size:cover reliably, object-fit less so.
                    <div key={i} className="mcf-photos__cell" style={{ backgroundImage: `url(${src})` }} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── 3. Declarations & consent ── */}
        <div className="mcf-sec">
          <div className="mcf-sec__tab">3. DECLARATIONS &amp; CONSENT</div>
          <div className="mcf-sec__box" style={{ padding: '8px 18px 10px' }}>
            <Declaration>
              I confirm that I am the sole owner / lawful possessor of the above jewellery and I am authorized to sell the same.
            </Declaration>
            <Declaration>
              I have been informed that Auriksha will melt the above jewellery for the purpose of purity testing / assay and accurate valuation.
            </Declaration>
            <Declaration>
              I voluntarily authorize Auriksha and its representatives to melt the jewellery in my presence.
            </Declaration>
            <Declaration>
              I understand that once the jewellery is melted, it cannot be returned in its original form.
            </Declaration>
            <Declaration>
              I agree that the final valuation and price will be based on the post-melting purity / assay result and the net {metalLower} weight
              obtained, as per Auriksha&rsquo;s standard process and prevailing market rate on the date of transaction.
            </Declaration>
            <Declaration>
              I consent to Auriksha testing the melted {metalLower} for purity using approved methods (e.g., XRF / Fire Assay / Other methods).
            </Declaration>
            <Declaration>
              I have read, understood and agree to the terms mentioned above.
            </Declaration>

            <div className="mcf-note">
              <span className="mcf-note__icon"><ShieldIcon /></span>
              <span className="mcf-note__text">
                <span className="mcf-note__lead">Note:</span> For transparency and security, the melting and testing process may be
                recorded through CCTV in your presence.
              </span>
            </div>
          </div>
        </div>

        {/* ── 4. Signatures ── */}
        <div className="mcf-sec">
          <div className="mcf-sec__tab">4. SIGNATURES</div>
          <div className="mcf-sec__box mcf-sec__box--flush">
            <div className="mcf-signs">
              <div className="mcf-sign">
                <div className="mcf-sign__title">Customer Signature /<br />Thumb Impression</div>
                <div className="mcf-sign__space" />
                <div className="mcf-sign__line"><span>Name :</span><i /></div>
              </div>
              <div className="mcf-sign">
                <div className="mcf-sign__title">Auriksha Employee Signature</div>
                <div className="mcf-sign__space" />
                <div className="mcf-sign__line"><span>Name :</span><i>{employeeName}</i></div>
                <div className="mcf-sign__line"><span>Emp. ID :</span><i>{employeeCode}</i></div>
                <div className="mcf-sign__line"><span>Branch :</span><i>{branchName}</i></div>
              </div>
              <div className="mcf-sign">
                <div className="mcf-sign__title">
                  Witness Signature
                  <span className="mcf-sign__hint">(For transactions above &#8377;1,00,000)</span>
                </div>
                <div className="mcf-sign__space" />
                <div className="mcf-sign__line"><span>Name :</span><i /></div>
                <div className="mcf-sign__line"><span>Mobile No. :</span><i /></div>
                <img className="mcf-sign__watermark" src={LOGO} alt="" crossOrigin="anonymous" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Footer ── */}
      <div className="mcf-foot">
        <div className="mcf-foot__thanks">
          <div className="mcf-foot__flourish"><Flourish /></div>
          <div className="mcf-foot__script">Thank you for choosing Auriksha.</div>
          <div className="mcf-foot__strap">Your trust is our greatest asset.</div>
        </div>
        <div className="mcf-foot__bar">
          <span className="mcf-foot__item"><PhoneIcon />{companyInfo.phone}</span>
          <span className="mcf-foot__item"><GlobeIcon />{website}</span>
          <span className="mcf-foot__item"><PinIcon />The Most Trusted Gold Buyer</span>
        </div>
      </div>
    </div>
  );
});

MeltingConsentDocument.displayName = 'MeltingConsentDocument';

export default MeltingConsentDocument;
